import { useState, useEffect, useCallback } from 'react';
import { quotesAPI } from '../../services/api';
import { fmt } from '../../utils/format';

// -- Resumen de costos a nivel cotizacion (manager-only) ------------------
// Consume GET /quotes/:id/cost-summary.
//
// Estructura por BLOQUES, igual que el Excel de Melissa: cada bloque cierra
// con su subtotal, para que cuando algo no cuadre se sepa donde buscar.
// Columnas Qty / Unit / $ per unit: ella cotiza asi (48 sf x $39, 40.8 sf x
// $8.91, 12 h x $38), y sin el precio unitario no hay como comparar contra
// su lista.
//
// TARIFFS es el unico renglon con dos tasas: 0.375 al costo (lo que paga
// fabrica) y 0.30 a la venta (lo que se le cobra al cliente). Su venta NO
// es costo/(1-NP) como los demas conceptos -- migr 082.
//
// Dos avisos:
//   is_stale -> el quote tiene precios viejos; el panel recalcula a precios
//               de HOY y difiere del PDF guardado. No re-persiste.
//   aluminio -> el costo de aluminio es estimado (margen embebido en el DDP
//               por linea), asi que el margen es preliminar.

const C = (n) => (n == null ? '-' : fmt.currency(n));
const Q = (n) => (n == null ? '' : (Math.round(n * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const U = (n) => (n == null ? '' : '$' + (Math.round(n * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const PB = { display: 'flex', justifyContent: 'space-between', padding: '3px 0' };

const TD    = { padding: '2px 0' };
const TDR   = { padding: '2px 0', textAlign: 'right' };
const TDMUT = { padding: '2px 0', textAlign: 'right', color: 'var(--text-2)' };
const THEAD = { fontSize: 10, fontWeight: 700, color: 'var(--text-2)', textAlign: 'right', padding: '0 0 3px' };

// Un renglon de concepto con sus columnas. muted lo pinta tenue (aluminio).
// Traduce precio_origen a algo que JL entienda sin abrir la base.
const SRC = (v) => (
  v === 'catalogo' ? 'Doorwin catalog' :
  v === 'curva'    ? 'Curve (no precedent)' :
  v === 'fabrica'  ? 'Factory quote' :
  (v || '-')
);

// Un renglon de concepto con sus columnas.
//   is_subtotal  -> cierra un tramo (Landed): borde arriba, seminegrita
//   is_total     -> cierra el bloque (Venta): borde doble, negrita
//   sin_desglose -> no hay FOB que auditar: tenue e italica
//   nota         -> texto del backend, visible via title (G.1 vive aqui)
function Row({ r, muted }) {
  const base =
    r.is_total    ? { fontWeight: 700, borderTop: '2px solid var(--border)' } :
    r.is_subtotal ? { fontWeight: 600, borderTop: '1px solid var(--border)' } :
    r.sin_desglose ? { color: 'var(--text-2)', fontStyle: 'italic' } :
    muted ? { color: 'var(--text-2)' } : undefined;
  const cell = { ...TD, ...(base || {}) };
  const cellR = { ...TDR, ...(base || {}) };
  const cellM = { ...TDMUT, ...(base || {}) };
  return (
    <tr>
      <td style={cell}>
        {r.concepto}
        {r.dual_rate && (
          <span
            title="Dos tasas sobre la misma base: 37.5% al costo (fabrica), 30% a la venta (cliente)"
            style={{ marginLeft: 5, fontSize: 10, color: 'var(--text-2)', cursor: 'help' }}
          >
            (2 rates)
          </span>
        )}
        {r.nota && (
          <span title={r.nota}
            style={{ marginLeft: 5, fontSize: 10, color: 'var(--text-2)', cursor: 'help' }}>
            &#9432;
          </span>
        )}
      </td>
      <td style={cellM}>{Q(r.qty)}</td>
      <td style={{ ...cellM, textAlign: 'left', paddingLeft: 4 }}>{r.unit || ''}</td>
      <td style={cellM}>{U(r.unit_cost)}</td>
      <td style={cellR}>{C(r.costo)}</td>
      <td style={cellR}>{C(r.venta)}</td>
    </tr>
  );
}

// Renglon de subtotal de bloque.
function SubRow({ label, costo, venta }) {
  const s = { padding: '4px 0', fontWeight: 600, borderTop: '1px solid var(--border)' };
  return (
    <tr>
      <td style={s}>{label}</td>
      <td style={s} /><td style={s} /><td style={s} />
      <td style={{ ...s, textAlign: 'right' }}>{C(costo)}</td>
      <td style={{ ...s, textAlign: 'right' }}>{C(venta)}</td>
    </tr>
  );
}

// Encabezado de bloque dentro de la tabla.
function BlockHead({ label }) {
  return (
    <tr>
      <td colSpan={6} style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-2)', padding: '8px 0 2px' }}>
        {label}
      </td>
    </tr>
  );
}

export default function CostSummaryCard({ quoteId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [open, setOpen]       = useState(false);
  const [aluDetail, setAluDetail] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await quotesAPI.costSummary(quoteId);
      setData(data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not load cost summary');
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  // H.19c: NP objetivo editable (manager). El endpoint ya es manager-only.
  const [npDraft, setNpDraft]   = useState('');
  const [npSaving, setNpSaving] = useState(false);
  const [npError, setNpError]   = useState(null);

  const saveNp = useCallback(async () => {
    const v = parseFloat(npDraft) / 100;
    if (!(v >= 0 && v < 1)) { setNpError('Enter a value between 0 and 99.9'); return; }
    setNpSaving(true); setNpError(null);
    try {
      await quotesAPI.update(quoteId, { np_pct: v });
      setData(null);
    } catch (e) {
      setNpError(e?.response?.data?.error || 'Could not save');
    } finally {
      setNpSaving(false);
    }
  }, [npDraft, quoteId]);

  // Gastos a nivel cotizacion: bono, millas, gasolina. Pass-through al cliente,
  // entran al precio SIN NP. Vienen fijos por default y el manager los edita.
  const IN = { textAlign: 'right', padding: '2px 6px', color: 'var(--text)',
               background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4 };
  const [gDraft, setGDraft]   = useState(null);
  const [gSaving, setGSaving] = useState(false);
  const [gError, setGError]   = useState(null);

  const saveGastos = useCallback(async () => {
    setGSaving(true); setGError(null);
    try {
      const n = (v) => (v === '' || v == null ? 0 : Number(v));
      await quotesAPI.update(quoteId, {
        bonus_qty:  n(gDraft.bonus_qty),  bonus_rate: n(gDraft.bonus_rate),
        trip_miles: n(gDraft.trip_miles), trip_rate:  n(gDraft.trip_rate),
        gas_amount: n(gDraft.gas_amount),
      });
      setData(null);
    } catch (e) {
      setGError(e?.response?.data?.error || 'Could not save');
    } finally {
      setGSaving(false);
    }
  }, [gDraft, quoteId]);

  useEffect(() => { if (open && !data && !error) load(); }, [open, data, error, load]);

  useEffect(() => {
    if (data && data.np_pct != null) {
      setNpDraft(String(parseFloat((parseFloat(data.np_pct) * 100).toFixed(2))));
    }
  }, [data]);

  useEffect(() => {
    if (data && data.gastos) {
      const g = { ...data.gastos };
      if (!(Number(g.bonus_rate) > 0) && Number(g.bonus_rate_default) > 0) {
        g.bonus_rate = g.bonus_rate_default;
      }
      setGDraft(g);
    }
  }, [data]);

  const hasAlu = data && Array.isArray(data.aluminum) && data.aluminum.length > 0;
  const hasIron = data && Array.isArray(data.products) && data.products.some(p => Math.abs(p.costo || 0) > 0.005 || Math.abs(p.venta || 0) > 0.005);
  const hasAluLineas = data && Array.isArray(data.aluminum_lineas) && data.aluminum_lineas.length > 0;
  const t      = data?.totals;
  const warnBox = {
    background: 'var(--warn-bg, #fff4e0)', color: 'var(--warn-fg, #9a6400)',
    border: '1px solid var(--warn-border, #f0c27b)', borderRadius: 6,
    padding: '6px 8px', marginBottom: 8, fontSize: 11, lineHeight: 1.4,
  };

  return (
    // Al abrirse, el panel se ensancha hacia la IZQUIERDA con margen
    // negativo: vive en la columna de 320px del grid de QuoteDetailPage,
    // y seis columnas ahi salen empalmadas. Asi gana ~420px sin tocar la
    // estructura de la pagina ni afectar lo que ve el vendedor.
    // El objetivo es que las columnas ALINEEN para compararlas contra el
    // Excel de Melissa -- por eso no se apila el detalle en otra linea.
    <div style={{
      marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10,

    }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          fontSize: 11, fontWeight: 600, color: 'var(--text-2)',
          display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        <span style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>&#9656;</span>
        COST SUMMARY (internal)
      </button>

      {open && (
        <div onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 59 }} aria-hidden="true" />
      )}
      {open && (
        <div style={{
          position: 'fixed', top: 70, right: 16, width: 760, maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 90px)', overflowY: 'auto', zIndex: 60,
          background: 'var(--bg, #fff)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: '0 8px 28px rgba(0,0,0,.18)', padding: '14px 16px',
        }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Cost summary (internal)</span>
          <button onClick={() => setOpen(false)}
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer',
                     color: 'var(--text-2)', lineHeight: 1, padding: '2px 8px', marginRight: -6 }} aria-label="Close">&times;</button>
        </div>
        <div style={{ marginTop: 8, fontSize: 12 }}>
          {loading && <div style={{ color: 'var(--text-2)' }}>Calculando...</div>}
          {error && <div style={{ color: 'var(--danger, #c0392b)' }}>{error}</div>}

          {data && !loading && !error && (
            <>
              {data.is_stale && (
                <div style={warnBox}>
                  Outdated prices. Recalculated at current prices; differs from the saved
                  PDF by <strong>{C(data.stale_delta)}</strong>. The signed quote was not modified.
                </div>
              )}

              {hasAlu && (
                <div style={warnBox}>
                  This quote includes aluminum. Aluminum product cost is a <strong>preliminary
                  estimate</strong> (derived from the recommended price; subject to the factory
                  quote), so the margin below is preliminary until the plant confirms.
                  Iron columns are final.
                </div>
              )}

              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '30%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '19%' }} />
                  <col style={{ width: '20%' }} />
                </colgroup>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={{ ...THEAD, textAlign: 'left' }}>ITEM</th>
                    <th style={THEAD}>QTY</th>
                    <th style={{ ...THEAD, textAlign: 'left', paddingLeft: 4 }}>UNIT</th>
                    <th style={THEAD}>$/UNIT</th>
                    <th style={THEAD}>COST</th>
                    <th style={THEAD}>SALE</th>
                  </tr>
                </thead>
                <tbody>
                  {hasIron && data.products.map((r, i) => <Row key={`p${i}`} r={r} />)}

                  {hasIron && data.products_subtotal && (
                    <SubRow
                      label="Products subtotal"
                      costo={data.products_subtotal.costo}
                      venta={data.products_subtotal.venta}
                    />
                  )}

                  {hasAlu && (
                    <>
                      <BlockHead label="ALUMINUM (Doorwin)" />
                      {data.aluminum.map((r, i) => <Row key={`a${i}`} r={r} />)}
                      {hasAluLineas && (
                        <tr>
                          <td colSpan={6} style={{ padding: '2px 0 8px' }}>
                            <button type="button" onClick={() => setAluDetail(d => !d)}
                              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                                       fontSize: 10, color: 'var(--text-2)' }}>
                              {aluDetail ? '\u25BE' : '\u25B8'} Detail by line ({data.aluminum_lineas.length})
                            </button>
                            {aluDetail && (
                              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 4, fontSize: 11 }}>
                                <thead>
                                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                    <th style={{ ...THEAD, textAlign: 'left' }}>LINE</th>
                                    <th style={{ ...THEAD, textAlign: 'left' }}>PRODUCT</th>
                                    <th style={THEAD}>QTY</th>
                                    <th style={THEAD}>SF</th>
                                    <th style={THEAD}>FOB/U</th>
                                    <th style={THEAD}>FOB TOTAL</th>
                                    <th style={THEAD}>LANDED TOTAL</th>
                                    <th style={{ ...THEAD, textAlign: 'left', paddingLeft: 6 }}>SOURCE</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {data.aluminum_lineas.map((d, i) => (
                                    <tr key={`ad${i}`} style={d.tiene_desglose ? undefined : { color: 'var(--text-2)' }}>
                                      <td style={TD}>{d.line_number}</td>
                                      <td style={TD}>
                                        {d.product_desc || ''}
                                        {d.location ? <span style={{ color: 'var(--text-2)' }}> - {d.location}</span> : null}
                                      </td>
                                      <td style={TDMUT}>{d.qty}</td>
                                      <td style={TDMUT}>{Q(d.total_sqft)}</td>
                                      <td style={TDMUT}>{d.fob_unit == null ? '-' : C(d.fob_unit)}</td>
                                      <td style={TDR}>{d.fob_total == null ? '-' : C(d.fob_total)}</td>
                                      <td style={TDR}>{d.landed_total == null ? '-' : C(d.landed_total)}</td>
                                      <td style={{ ...TD, paddingLeft: 6, color: 'var(--text-2)' }}>{SRC(d.precio_origen)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  )}

                  <BlockHead label="LABOR" />
                  {data.labor.map((r, i) => <Row key={`l${i}`} r={r} />)}
                </tbody>
              </table>

              {/* Gastos: pass-through, sin NP. Costo = venta. */}
              <div style={{ borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 4 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-2)', marginBottom: 2 }}>
                  EXPENSES (client covers)
                </div>
                {data.expenses.filter(r => /Comision/i.test(r.concepto)).map((r, i) => (
                  <div key={`e${i}`} style={{ ...PB }}>
                    <span style={{ color: r.missing ? 'var(--danger, #c0392b)' : 'inherit' }}>
                      {r.concepto}{r.missing ? ' - no rate' : ''}
                      {r.base != null && r.base > 0 && (
                        <span style={{ color: 'var(--text-2)', fontSize: 11, marginLeft: 5 }}>
                          on {C(r.base)}
                        </span>
                      )}
                    </span>
                    <span>{C(r.monto)}</span>
                  </div>
                ))}
                {gDraft && [
                  { k: 'Bonus', qk: 'bonus_qty',  rk: 'bonus_rate', qstep: '1',   extra: Number((data.gastos || {}).line_trip || 0) * 0 },
                  { k: 'Trip',  qk: 'trip_miles', rk: 'trip_rate',  qstep: '0.1', extra: Number((data.gastos || {}).line_trip || 0) },
                  { k: 'Gas',   qk: null,         rk: 'gas_amount', qstep: null,  extra: Number((data.gastos || {}).line_gas  || 0) },
                ].map(g => {
                  const qv = g.qk ? Number(gDraft[g.qk] || 0) : 1;
                  const monto = qv * Number(gDraft[g.rk] || 0) + g.extra;
                  return (
                    <div key={g.k} style={{ ...PB, alignItems: 'center' }}>
                      <span>{g.k}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {g.qk && (
                          <>
                            <input type="number" step={g.qstep} min="0" value={gDraft[g.qk]}
                              onChange={e => setGDraft(s => ({ ...s, [g.qk]: e.target.value }))}
                              disabled={gSaving} style={{ ...IN, width: 56 }} />
                            <span style={{ color: 'var(--text-2)', fontSize: 11 }}>x</span>
                          </>
                        )}
                        <span style={{ color: 'var(--text-2)' }}>$</span>
                        <input type="number" step="0.01" min="0" value={gDraft[g.rk]}
                          onChange={e => setGDraft(s => ({ ...s, [g.rk]: e.target.value }))}
                          disabled={gSaving} style={{ ...IN, width: 78 }} />
                        <span style={{ minWidth: 84, textAlign: 'right' }}>{C(monto)}</span>
                      </span>
                    </div>
                  );
                })}
                {gDraft && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '3px 0' }}>
                    <button type="button" onClick={saveGastos} disabled={gSaving}
                      style={{ fontSize: 11, padding: '2px 8px', cursor: 'pointer' }}>
                      {gSaving ? '...' : 'Save expenses'}
                    </button>
                  </div>
                )}
                {gError && (
                  <div style={{ fontSize: 11, color: 'var(--danger, #dc2626)', paddingBottom: 3 }}>{gError}</div>
                )}
                {(Number((data.gastos || {}).line_trip || 0) > 0 || Number((data.gastos || {}).line_gas || 0) > 0) && (
                  <div style={{ fontSize: 10, color: '#b45309', paddingBottom: 3 }}>
                    Hay trip/gas capturados a nivel linea. Esos llevan NP encima: hay que pasarlos a este nivel.
                  </div>
                )}
                <div style={{ ...PB, fontWeight: 600 }}>
                  <span>Total expenses</span><span>{C(data.expenses_total)}</span>
                </div>
              </div>

              {t && (
                <div style={{ borderTop: '2px solid var(--border)', marginTop: 6, paddingTop: 6,
                              opacity: hasAlu ? 0.55 : 1 }}>
                  <div style={{ ...PB }}>
                    <span>Sales (products + expenses, post-NP)</span><span>{C(t.sales_subtotal)}</span>
                  </div>
                  <div style={{ ...PB }}>
                    <span>Labor (post-NP)</span><span>{C(t.labor_subtotal)}</span>
                  </div>
                  <div style={{ ...PB, color: 'var(--text-2)' }}>
                    <span>Cost (base pre-NP)</span><span>{C(t.cost_total)}</span>
                  </div>
                  <div style={{ ...PB, alignItems: 'center' }}>
                    <span style={hasIron ? undefined : { color: 'var(--text-2)' }}>
                      NP target
                      {!hasIron && (
                        <span title="El NP objetivo gobierna el precio de FIERRO. El aluminio se cotiza con su propio margen (cm_pct), asi que este campo no mueve nada en una cotizacion de puro aluminio."
                          style={{ marginLeft: 5, fontSize: 10, cursor: 'help' }}>(iron only)</span>
                      )}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input type="number" step="0.1" min="0" max="99.9" value={npDraft}
                        onChange={e => setNpDraft(e.target.value)}
                        disabled={npSaving || !hasIron}
                        style={{ width: 78, textAlign: 'right', padding: '2px 6px', color: 'var(--text)',
                                 background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4 }} />
                      <span style={{ color: 'var(--text-2)' }}>%</span>
                      <button type="button" onClick={saveNp} disabled={npSaving || !hasIron}
                        style={{ fontSize: 11, padding: '2px 8px', cursor: 'pointer' }}>
                        {npSaving ? '...' : 'Save'}
                      </button>
                    </span>
                  </div>
                  {npError && (
                    <div style={{ fontSize: 11, color: 'var(--danger, #dc2626)', paddingBottom: 3 }}>
                      {npError}
                    </div>
                  )}
                  <div style={{ ...PB, fontWeight: 700, color: 'var(--success, #1f8b4c)' }}>
                    <span>Net Profit</span>
                    <span>{C(t.net_profit)} ({fmt.percent(t.np_realized)})</span>
                  </div>
                  <div style={{ ...PB, color: 'var(--text-2)' }}>
                    <span>Tax ({(parseFloat(t.tax_pct) * 100).toFixed(2).replace(/\.?0+$/, '')}%)</span><span>{C(t.tax_amount)}</span>
                  </div>
                  <div style={{ ...PB, fontWeight: 700, borderTop: '1px solid var(--border)',
                                marginTop: 3, paddingTop: 5 }}>
                    <span>SALE final</span><span>{C(t.grand_total)}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        </div>
      )}
    </div>
  );
}
